import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { MUSIC_TRACK_KIND, parseMusicTrack, type MusicTrack, type Playlist } from '@/lib/musicTypes';

export function usePlaylistTracks(playlist: Playlist | null | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['playlist-tracks', playlist?.id],
    enabled: !!playlist && playlist.trackRefs.length > 0,
    queryFn: async (c) => {
      if (!playlist) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Parse track refs into filters
      const filters = playlist.trackRefs.map((ref) => {
        const [kind, pubkey, d] = ref.split(':');
        return {
          kinds: [MUSIC_TRACK_KIND],
          authors: [pubkey],
          '#d': [d],
        };
      });

      // Query all tracks in parallel
      const events = await nostr.query(filters, { signal });

      // Parse tracks
      const trackMap = new Map<string, MusicTrack>();
      events.forEach((event) => {
        const track = parseMusicTrack(event);
        if (track) {
          const ref = `${MUSIC_TRACK_KIND}:${event.pubkey}:${track.id}`;
          trackMap.set(ref, track);
        }
      });

      // Return tracks in playlist order
      return playlist.trackRefs
        .map((ref) => trackMap.get(ref))
        .filter((track): track is MusicTrack => track !== null);
    },
  });
}
